import * as React from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { cn } from "@app/ui/lib/Utils";

export interface DropdownOption<T extends string | number> {
  value: T;
  label: string;
}

interface DropdownBaseProps<T extends string | number> {
  /** 트리거 버튼의 aria-label 및 목록의 aria-label로 쓰여요. */
  title: string;
  options: DropdownOption<T>[];
  className?: string;
  panelClassName?: string;
  /**
   * 주면 트리거가 라벨·chevron 대신 이 아이콘만 보이는 정사각형 아이콘 버튼이 돼요
   * (공개 링크 브랜딩 바처럼 공간이 좁을 때). aria-label 은 title 이 담당해요.
   */
  icon?: React.ReactNode;
  /** 옵션 목록을 트리거의 어느 쪽 끝에 맞출지. 아이콘 모드는 기본이 "end"(우상단 넘침 방지). */
  align?: "start" | "end";
  /** 옵션 목록 위에 검색 입력을 두고 label 기준으로 필터링해요. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** 옵션 목록의 최대 높이(px). 넘치면 스크롤돼요. */
  maxHeight?: number;
}

interface DropdownSingleProps<T extends string | number> extends DropdownBaseProps<T> {
  multiple?: false;
  value: T;
  onChange?: (value: T) => void;
}

interface DropdownMultiProps<T extends string | number> extends DropdownBaseProps<T> {
  /** 다중 선택 모드. 여러 값을 골라 필터로 쓸 때. */
  multiple: true;
  /** 선택된 값들. 빈 배열이면 "전체"를 뜻해요. */
  values: T[];
  onValuesChange?: (values: T[]) => void;
  /** 아무것도 선택되지 않았을 때 트리거에 보여줄 문구(기본은 title). */
  emptyLabel?: string;
}

export type DropdownProps<T extends string | number> =
  | DropdownSingleProps<T>
  | DropdownMultiProps<T>;

/** 배열에서 값을 토글한 새 배열. */
const toggle = <T,>(list: T[], value: T) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

/**
 * 트리거 버튼을 누르면 바로 아래에 옵션 목록이 뜨는 드롭다운.
 * `multiple` 모드에서는 여러 값을 골라 필터로 쓸 수 있고(빈 선택 = 전체),
 * 토글해도 목록이 닫히지 않아요.
 */
function Dropdown<T extends string | number>(props: DropdownProps<T>) {
  const { t } = useLingui();
  const {
    title,
    options,
    className,
    panelClassName,
    icon,
    align,
    searchable = false,
    searchPlaceholder,
    maxHeight,
  } = props;
  const isMulti = props.multiple === true;
  const resolvedSearchPlaceholder = searchPlaceholder ?? t`검색…`;
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const filteredOptions = React.useMemo(() => {
    if (!searchable) return options;
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) => option.label.toLowerCase().includes(query));
  }, [options, search, searchable]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch("");
  };

  const isSelected = (value: T) =>
    isMulti ? props.values.includes(value) : props.value === value;

  const handlePick = (value: T) => {
    if (isMulti) {
      props.onValuesChange?.(toggle(props.values, value));
      return;
    }
    props.onChange?.(value);
    handleOpenChange(false);
  };

  const triggerLabel = isMulti
    ? props.values.length === 0
      ? (props.emptyLabel ?? title)
      : options
          .filter((option) => props.values.includes(option.value))
          .map((option) => option.label)
          .join("/")
    : (options.find((option) => option.value === props.value)?.label ?? title);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange} modal>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={title}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            icon
              ? "flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-foreground transition-colors hover:bg-border"
              : "flex items-center gap-1.5 self-start rounded-md bg-muted px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-border",
            className
          )}
        >
          {icon ?? (
            <>
              {triggerLabel}
              <ChevronDownIcon className="size-4 text-muted-foreground" />
            </>
          )}
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="bottom"
          align={align ?? (icon ? "end" : "start")}
          sideOffset={4}
          className={cn(
            "z-50 flex w-fit min-w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden origin-[var(--radix-popover-content-transform-origin)] rounded-md bg-popover text-popover-foreground shadow-md duration-150 fill-mode-forwards pointer-events-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
            panelClassName
          )}
        >
          {searchable && (
            <div className="relative flex items-center border-b border-border">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 size-4 text-muted-foreground" />
              <input
                // 검색 가능한 드롭다운은 열릴 때 검색창 자동 포커스가 의도된 UX다.
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                aria-label={resolvedSearchPlaceholder}
                autoCapitalize="off"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                type="search"
                placeholder={resolvedSearchPlaceholder}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-9 w-full bg-transparent pl-8 pr-2.5 text-sm text-foreground placeholder:text-muted-foreground"
              />
            </div>
          )}
          <div
            role="listbox"
            aria-label={title}
            aria-multiselectable={isMulti || undefined}
            className="flex flex-col overflow-y-auto p-1"
            style={maxHeight ? { maxHeight } : undefined}
          >
            {filteredOptions.length === 0 ? (
              <p className="px-2.5 py-3 text-center text-sm text-muted-foreground">
                <Trans>검색 결과가 없어요.</Trans>
              </p>
            ) : (
              filteredOptions.map((option) => {
                const active = isSelected(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => handlePick(option.value)}
                    className={cn(
                      "flex w-full items-center gap-2 whitespace-nowrap rounded px-2.5 py-2 text-left text-sm font-medium transition-colors",
                      active
                        ? "bg-primary text-primary-foreground"
                        : "text-foreground hover:bg-muted"
                    )}
                  >
                    <span className="flex-1">{option.label}</span>
                    {isMulti && active && <CheckIcon className="size-4 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export { Dropdown };
